// ==============================================
// Settings Page
// ==============================================

import React, { useState, useMemo, useRef } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { CheckIcon, ChevronDownIcon, ExclamationTriangleIcon, CheckBadgeIcon } from '@heroicons/react/24/solid';
import { api } from '../../services/api';
import { useAuth } from '../../providers/AuthProvider';
import { useLogout } from '../../hooks/useLogout';
import { resendVerificationEmail, changeEmail, changePassword } from '../../lib/auth-client';
import { useToastStore } from '../../store/toastStore';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { useViewport } from '../../providers/ViewportProvider';
import { useRevealFocusedFieldInDocument } from '../../lib/viewport/useRevealFocusedFieldInDocument';

// Email preferences type
interface EmailPreferences {
  order_notifications: boolean;
  subscription_notifications: boolean;
  billing_notifications: boolean;
  usage_notifications: boolean;
  marketing_emails: boolean;
}

// Country codes for phone number dropdown
const COUNTRY_CODES = [
  { code: '+1', country: 'US', flag: '🇺🇸', name: 'United States' },
  { code: '+1', country: 'CA', flag: '🇨🇦', name: 'Canada' },
  { code: '+44', country: 'GB', flag: '🇬🇧', name: 'United Kingdom' },
  { code: '+61', country: 'AU', flag: '🇦🇺', name: 'Australia' },
  { code: '+64', country: 'NZ', flag: '🇳🇿', name: 'New Zealand' },
  { code: '+49', country: 'DE', flag: '🇩🇪', name: 'Germany' },
  { code: '+33', country: 'FR', flag: '🇫🇷', name: 'France' },
  { code: '+39', country: 'IT', flag: '🇮🇹', name: 'Italy' },
  { code: '+34', country: 'ES', flag: '🇪🇸', name: 'Spain' },
  { code: '+31', country: 'NL', flag: '🇳🇱', name: 'Netherlands' },
  { code: '+47', country: 'NO', flag: '🇳🇴', name: 'Norway' },
  { code: '+46', country: 'SE', flag: '🇸🇪', name: 'Sweden' },
  { code: '+45', country: 'DK', flag: '🇩🇰', name: 'Denmark' },
  { code: '+358', country: 'FI', flag: '🇫🇮', name: 'Finland' },
  { code: '+81', country: 'JP', flag: '🇯🇵', name: 'Japan' },
  { code: '+82', country: 'KR', flag: '🇰🇷', name: 'South Korea' },
  { code: '+86', country: 'CN', flag: '🇨🇳', name: 'China' },
  { code: '+91', country: 'IN', flag: '🇮🇳', name: 'India' },
  { code: '+65', country: 'SG', flag: '🇸🇬', name: 'Singapore' },
  { code: '+852', country: 'HK', flag: '🇭🇰', name: 'Hong Kong' },
  { code: '+971', country: 'AE', flag: '🇦🇪', name: 'UAE' },
  { code: '+966', country: 'SA', flag: '🇸🇦', name: 'Saudi Arabia' },
  { code: '+27', country: 'ZA', flag: '🇿🇦', name: 'South Africa' },
  { code: '+55', country: 'BR', flag: '🇧🇷', name: 'Brazil' },
  { code: '+52', country: 'MX', flag: '🇲🇽', name: 'Mexico' },
];

// Parse phone number into country code and number
function parsePhoneNumber(phone: string | null | undefined): { countryCode: string; phoneNumber: string } {
  if (!phone) return { countryCode: '+1', phoneNumber: '' };

  // Try to match known country codes (sorted by length descending to match longer codes first)
  const sortedCodes = [...COUNTRY_CODES].sort((a, b) => b.code.length - a.code.length);

  for (const { code } of sortedCodes) {
    if (phone.startsWith(code)) {
      return {
        countryCode: code,
        phoneNumber: phone.slice(code.length).trim(),
      };
    }
  }

  // Default to US if no match
  return { countryCode: '+1', phoneNumber: phone.replace(/^\+\d+\s*/, '') };
}

function ChangeEmailField({ email, verified, scoutLimited }: { email?: string; verified?: boolean; scoutLimited?: boolean }) {
  const [editing, setEditing] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const queryClient = useQueryClient();
  const { refreshUser } = useAuth();

  const handleSave = async () => {
    if (!newEmail || newEmail === email || submitting) return;
    setSubmitting(true);
    try {
      const callbackURL = `${window.location.origin}/dashboard?verified=true`;
      await changeEmail({ newEmail, callbackURL });
      // Every account keeps its current address until the link sent to the
      // new one is opened (the API proves each new address first). Refresh
      // anyway so the page matches the server.
      await refreshUser();
      queryClient.invalidateQueries({ queryKey: ['user'] });
      setEditing(false);
      setNewEmail('');
      useToastStore.getState().addToast({
        type: 'success',
        title: 'Check your new inbox',
        message: `We've sent a link to ${newEmail}. Your email changes when you open it.`,
      });
    } catch (err: any) {
      const msg = err?.message || err?.data?.message || 'Could not change email.';
      useToastStore.getState().addToast({ type: 'error', title: 'Email change failed', message: msg });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <label htmlFor="email" className="block text-sm font-medium text-slate-400 mb-1">
        Email
      </label>
      {editing ? (
        <div className="space-y-2">
          <input
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="New email address"
            autoFocus
            className="w-full px-4 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:border-cyan-500 focus:outline-none transition-colors"
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleSave}
              disabled={submitting || !newEmail || newEmail === email}
              className="px-3 py-1.5 text-xs font-medium bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg hover:from-cyan-400 hover:to-blue-500 transition-all disabled:opacity-50"
            >
              {submitting ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => { setEditing(false); setNewEmail(''); }}
              className="px-3 py-1.5 text-xs font-medium text-slate-400 hover:text-white transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <input
            type="email"
            id="email"
            value={email || ''}
            disabled
            className="flex-1 min-w-0 px-4 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-slate-400 cursor-not-allowed truncate"
          />
          {!scoutLimited && (
            <button
              type="button"
              onClick={() => { setEditing(true); setNewEmail(''); }}
              className="shrink-0 px-3 py-2 text-xs font-medium text-cyan-400 hover:text-cyan-300 border border-slate-600 hover:border-cyan-500/50 rounded-lg transition-colors"
            >
              Change
            </button>
          )}
        </div>
      )}
      {scoutLimited && (
        <p className="mt-2 text-sm text-slate-400">
          To change your email, sign in to MyPolaris on your phone or computer.
        </p>
      )}
      <EmailVerificationStatus email={email} verified={verified} />
    </div>
  );
}

function ChangePasswordSection() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [touched, setTouched] = useState(false);

  // Independent document-scroll reveal scope for the Change Password form: only a
  // field focused inside this form triggers it, and the reveal keeps the form's
  // submit action (companion) visible with the focused field. Document/body stays
  // the sole scroll owner — this hook never introduces a scroll container.
  const formRef = useRef<HTMLFormElement>(null);
  const submitRef = useRef<HTMLButtonElement>(null);
  useRevealFocusedFieldInDocument(formRef, { companionRef: submitRef });

  const passwordsMatch = newPassword === confirmPassword;
  const newLongEnough = newPassword.length >= 8;
  const canSubmit = currentPassword && newLongEnough && passwordsMatch && !submitting;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await changePassword({ currentPassword, newPassword, revokeOtherSessions: true });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      useToastStore.getState().addToast({
        type: 'success',
        title: 'Password changed',
        message: 'Your password has been updated and other sessions have been signed out.',
      });
    } catch (err: any) {
      const code = err?.data?.code || err?.code || '';
      const msg = code === 'INVALID_PASSWORD'
        ? 'Current password is incorrect.'
        : code === 'CREDENTIAL_ACCOUNT_NOT_FOUND'
          ? 'No password is set for this account. You signed in with a social provider.'
          : err?.message || 'Could not change password.';
      useToastStore.getState().addToast({ type: 'error', title: 'Password change failed', message: msg });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form ref={formRef} onSubmit={handleSubmit} className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
      <h2 className="text-lg font-semibold text-white mb-4">Change Password</h2>

      <div className="space-y-4">
        <div>
          <label htmlFor="currentPassword" className="block text-sm font-medium text-slate-400 mb-1">
            Current Password
          </label>
          <div className="relative">
            <input
              type={showCurrent ? 'text' : 'password'}
              id="currentPassword"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className={`w-full px-4 py-2 pr-10 bg-slate-900/50 border rounded-lg text-white focus:border-cyan-500 focus:outline-none transition-colors ${
                touched && !currentPassword ? 'border-red-500' : 'border-slate-600'
              }`}
              autoComplete="current-password"
            />
            <button
              type="button"
              onClick={() => setShowCurrent(!showCurrent)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition-colors"
            >
              {showCurrent ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
            </button>
          </div>
          {touched && !currentPassword && (
            <p className="mt-1 text-xs text-red-400">Current password is required</p>
          )}
        </div>

        <div>
          <label htmlFor="newPassword" className="block text-sm font-medium text-slate-400 mb-1">
            New Password
          </label>
          <div className="relative">
            <input
              type={showNew ? 'text' : 'password'}
              id="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full px-4 py-2 pr-10 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:border-cyan-500 focus:outline-none transition-colors"
              autoComplete="new-password"
              placeholder="Minimum 8 characters"
            />
            <button
              type="button"
              onClick={() => setShowNew(!showNew)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition-colors"
            >
              {showNew ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
            </button>
          </div>
          {newPassword && !newLongEnough && (
            <p className="mt-1 text-xs text-amber-400">Password must be at least 8 characters</p>
          )}
        </div>

        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-slate-400 mb-1">
            Confirm New Password
          </label>
          <input
            type="password"
            id="confirmPassword"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className={`w-full px-4 py-2 bg-slate-900/50 border rounded-lg text-white focus:border-cyan-500 focus:outline-none transition-colors ${
              touched && !confirmPassword ? 'border-red-500' : 'border-slate-600'
            }`}
            autoComplete="new-password"
          />
          {(confirmPassword && !passwordsMatch) && (
            <p className="mt-1 text-xs text-red-400">Passwords do not match</p>
          )}
          {touched && !confirmPassword && (
            <p className="mt-1 text-xs text-red-400">Please confirm your new password</p>
          )}
        </div>
      </div>

      <div className="mt-6">
        <button
          ref={submitRef}
          type="submit"
          disabled={!canSubmit}
          className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg hover:from-cyan-400 hover:to-blue-500 transition-all disabled:opacity-50"
        >
          {submitting ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Changing…
            </>
          ) : (
            'Change Password'
          )}
        </button>
      </div>
    </form>
  );
}

function EmailVerificationStatus({ email, verified }: { email?: string; verified?: boolean }) {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  if (verified) {
    return (
      <div className="mt-1 flex items-center gap-1.5">
        <CheckBadgeIcon className="w-4 h-4 text-green-400" />
        <span className="text-xs text-green-400">Verified</span>
      </div>
    );
  }

  const handleResend = async () => {
    if (!email || sending || sent) return;
    setSending(true);
    try {
      const callbackURL = `${window.location.origin}/dashboard?verified=true`;
      await resendVerificationEmail(email, callbackURL);
      setSent(true);
      useToastStore.getState().addToast({
        type: 'success',
        title: 'Verification email sent',
        message: 'Check your inbox for the confirmation link.',
      });
    } catch {
      useToastStore.getState().addToast({
        type: 'error',
        title: 'Failed to send',
        message: 'Could not send verification email. Please try again later.',
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mt-1 flex items-center gap-2">
      <ExclamationTriangleIcon className="w-4 h-4 text-amber-400 shrink-0" />
      <span className="text-xs text-amber-400">Not verified</span>
      <button
        type="button"
        onClick={handleResend}
        disabled={sending || sent}
        className="text-xs text-cyan-400 hover:text-cyan-300 underline underline-offset-2 disabled:text-slate-500 disabled:no-underline transition-colors"
      >
        {sent ? 'Sent — check your inbox' : sending ? 'Sending…' : 'Resend verification email'}
      </button>
    </div>
  );
}

export function SettingsPage() {
  const { user, refreshUser } = useAuth();
  const logout = useLogout();
  const queryClient = useQueryClient();

  // Keyboard geometry (normalized) drives only the keyboard-open trailing space
  // reserved below; the page keeps document/body as its sole scroll owner.
  const { keyboardVisible } = useViewport();

  // Independent document-scroll reveal scope for the Profile Information form:
  // only a field focused inside this form triggers it, revealing that field with
  // the Profile "Save Changes" action (companion) kept visible.
  const profileFormRef = useRef<HTMLFormElement>(null);
  const profileSaveRef = useRef<HTMLButtonElement>(null);
  useRevealFocusedFieldInDocument(profileFormRef, { companionRef: profileSaveRef });

  const [name, setName] = useState(user?.name || '');
  const [company, setCompany] = useState(user?.company || '');

  // Parse existing phone number into country code and number
  const parsedPhone = useMemo(() => parsePhoneNumber(user?.phone), [user?.phone]);
  const [countryCode, setCountryCode] = useState(parsedPhone.countryCode);
  const [phoneNumber, setPhoneNumber] = useState(parsedPhone.phoneNumber);

  const [saved, setSaved] = useState(false);

  const updateMutation = useMutation({
    mutationFn: (data: { name: string; company: string; phone: string }) =>
      api.updateProfile(data),
    onSuccess: () => {
      // Keep the auth context in sync so the rest of the app (e.g. the
      // dashboard welcome banner) reflects the change without a reload.
      refreshUser();
      queryClient.invalidateQueries({ queryKey: ['user'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    },
  });

  // Email preferences query and mutation
  const { data: emailPreferences, isLoading: preferencesLoading } = useQuery({
    queryKey: ['emailPreferences'],
    queryFn: () => api.getEmailPreferences(),
  });

  const updatePreferencesMutation = useMutation({
    mutationFn: (preferences: Partial<EmailPreferences>) =>
      api.updateEmailPreferences(preferences),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emailPreferences'] });
    },
  });

  const handlePreferenceChange = (key: keyof EmailPreferences, value: boolean) => {
    updatePreferencesMutation.mutate({ [key]: value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Combine country code and phone number
    const fullPhone = phoneNumber ? `${countryCode} ${phoneNumber}` : '';
    updateMutation.mutate({ name, company, phone: fullPhone });
  };

  // Get unique country codes for the dropdown (avoid duplicates like +1 for US/CA)
  const uniqueCountryCodes = useMemo(() => {
    const seen = new Map<string, typeof COUNTRY_CODES[0]>();
    COUNTRY_CODES.forEach((item) => {
      if (!seen.has(item.code)) {
        seen.set(item.code, item);
      }
    });
    return Array.from(seen.values());
  }, []);

  return (
    // Keyboard-open trailing space so the lowest fields / submit can scroll clear of
    // the keyboard. The `pb-[var(--vortex-keyboard-inset)]` class applies the
    // normalized inset CSS variable (no arithmetic, no hard-coded height) only while
    // the keyboard is visible; it is dropped entirely when hidden, so no gap remains.
    // Document/body stays the sole scroll owner — this only grows the page's height.
    <div className={`space-y-6 max-w-2xl${keyboardVisible ? ' pb-[var(--vortex-keyboard-inset)]' : ''}`}>
      <div>
        <h1 className="text-2xl font-bold text-white">Settings</h1>
        <p className="text-slate-400">Manage your account settings</p>
      </div>

      {/* Profile Settings */}
      <form ref={profileFormRef} onSubmit={handleSubmit} className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
        <h2 className="text-lg font-semibold text-white mb-4">Profile Information</h2>

        <div className="space-y-4">
          <ChangeEmailField email={user?.email} verified={user?.email_verified} scoutLimited={user?.signed_in_by_scout === true} />

          <div>
            <label htmlFor="name" className="block text-sm font-medium text-slate-400 mb-1">
              Full Name
            </label>
            <input
              type="text"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:border-cyan-500 focus:outline-none transition-colors"
              placeholder="Your name"
            />
          </div>

          <div>
            <label htmlFor="company" className="block text-sm font-medium text-slate-400 mb-1">
              Company
            </label>
            <input
              type="text"
              id="company"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              className="w-full px-4 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:border-cyan-500 focus:outline-none transition-colors"
              placeholder="Your company"
            />
          </div>

          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-slate-400 mb-1">
              Phone
            </label>
            <div className="flex gap-2">
              {/* Country Code Dropdown */}
              <div className="relative">
                <select
                  id="countryCode"
                  value={countryCode}
                  onChange={(e) => setCountryCode(e.target.value)}
                  className="appearance-none w-[110px] px-3 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:border-cyan-500 focus:outline-none transition-colors cursor-pointer pr-8"
                >
                  {uniqueCountryCodes.map((item) => (
                    <option key={`${item.code}-${item.country}`} value={item.code}>
                      {item.flag} {item.code}
                    </option>
                  ))}
                </select>
                <ChevronDownIcon className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              </div>

              {/* Phone Number Input */}
              <input
                type="tel"
                id="phone"
                value={phoneNumber}
                onChange={(e) => {
                  // Only allow numbers, spaces, dashes, and parentheses
                  const value = e.target.value.replace(/[^\d\s\-()]/g, '');
                  setPhoneNumber(value);
                }}
                className="flex-1 px-4 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:border-cyan-500 focus:outline-none transition-colors"
                placeholder="(555) 000-0000"
              />
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-center gap-4">
          <button
            ref={profileSaveRef}
            type="submit"
            disabled={updateMutation.isPending}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg hover:from-cyan-400 hover:to-blue-500 transition-all disabled:opacity-50"
          >
            {updateMutation.isPending ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Saving...
              </>
            ) : saved ? (
              <>
                <CheckIcon className="w-5 h-5" />
                Saved
              </>
            ) : (
              'Save Changes'
            )}
          </button>

          {updateMutation.isError && (
            <p className="text-sm text-red-400">Failed to save changes</p>
          )}
        </div>
      </form>

      {/* Change Password */}
      <ChangePasswordSection />

      {/* Account Actions */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
        <h2 className="text-lg font-semibold text-white mb-4">Account</h2>

        <div className="space-y-4">
          <div className="flex items-center justify-between gap-4 p-4 bg-slate-900/50 rounded-lg">
            <div className="min-w-0">
              <p className="text-sm font-medium text-white">Sign Out</p>
              <p className="text-xs text-slate-400">Sign out of your account on this device</p>
            </div>
            <button
              onClick={() => void logout()}
              className="shrink-0 px-4 py-2 text-sm font-medium text-slate-400 hover:text-white transition-colors"
            >
              Sign Out
            </button>
          </div>

          <div className="flex items-center justify-between gap-4 p-4 bg-red-500/5 border border-red-500/20 rounded-lg">
            <div className="min-w-0">
              <p className="text-sm font-medium text-red-400">Delete Account</p>
              <p className="text-xs text-slate-400">Permanently delete your account and all data</p>
            </div>
            <button
              onClick={() => alert('Please contact support to delete your account.')}
              className="shrink-0 px-4 py-2 text-sm font-medium text-red-400 hover:text-red-300 transition-colors"
            >
              Delete
            </button>
          </div>
        </div>
      </div>

      {/* Email Preferences */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
        <h2 className="text-lg font-semibold text-white mb-4">Email Notifications</h2>
        <p className="text-sm text-slate-400 mb-6">Choose which emails you'd like to receive</p>

        {preferencesLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-6 h-6 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Order Notifications */}
            <div className="flex items-center justify-between gap-4 p-4 bg-slate-900/50 rounded-lg">
              <div className="min-w-0">
                <p className="text-sm font-medium text-white">Order Updates</p>
                <p className="text-xs text-slate-400">Emails when orders are confirmed, processing, completed, or failed</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer shrink-0">
                <input
                  type="checkbox"
                  checked={emailPreferences?.order_notifications ?? true}
                  onChange={(e) => handlePreferenceChange('order_notifications', e.target.checked)}
                  disabled={updatePreferencesMutation.isPending}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-slate-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-cyan-500 peer-disabled:opacity-50"></div>
              </label>
            </div>

            {/* Subscription Notifications */}
            <div className="flex items-center justify-between gap-4 p-4 bg-slate-900/50 rounded-lg">
              <div className="min-w-0">
                <p className="text-sm font-medium text-white">Subscription Updates</p>
                <p className="text-xs text-slate-400">Emails about subscription changes, renewals, and cancellations</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer shrink-0">
                <input
                  type="checkbox"
                  checked={emailPreferences?.subscription_notifications ?? true}
                  onChange={(e) => handlePreferenceChange('subscription_notifications', e.target.checked)}
                  disabled={updatePreferencesMutation.isPending}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-slate-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-cyan-500 peer-disabled:opacity-50"></div>
              </label>
            </div>

            {/* Billing Notifications */}
            <div className="flex items-center justify-between gap-4 p-4 bg-slate-900/50 rounded-lg">
              <div className="min-w-0">
                <p className="text-sm font-medium text-white">Billing & Invoices</p>
                <p className="text-xs text-slate-400">Invoices, receipts, and payment-related notifications</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer shrink-0">
                <input
                  type="checkbox"
                  checked={emailPreferences?.billing_notifications ?? true}
                  onChange={(e) => handlePreferenceChange('billing_notifications', e.target.checked)}
                  disabled={updatePreferencesMutation.isPending}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-slate-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-cyan-500 peer-disabled:opacity-50"></div>
              </label>
            </div>

            {/* Usage Notifications */}
            <div className="flex items-center justify-between gap-4 p-4 bg-slate-900/50 rounded-lg">
              <div className="min-w-0">
                <p className="text-sm font-medium text-white">Usage Alerts</p>
                <p className="text-xs text-slate-400">Alerts when token usage reaches 75%, 90%, or 100% of your quota</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer shrink-0">
                <input
                  type="checkbox"
                  checked={emailPreferences?.usage_notifications ?? true}
                  onChange={(e) => handlePreferenceChange('usage_notifications', e.target.checked)}
                  disabled={updatePreferencesMutation.isPending}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-slate-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-cyan-500 peer-disabled:opacity-50"></div>
              </label>
            </div>

            {/* Marketing Emails */}
            <div className="flex items-center justify-between gap-4 p-4 bg-slate-900/50 rounded-lg border border-slate-700">
              <div className="min-w-0">
                <p className="text-sm font-medium text-white">Marketing & Updates</p>
                <p className="text-xs text-slate-400">News, product updates, and promotional offers</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer shrink-0">
                <input
                  type="checkbox"
                  checked={emailPreferences?.marketing_emails ?? false}
                  onChange={(e) => handlePreferenceChange('marketing_emails', e.target.checked)}
                  disabled={updatePreferencesMutation.isPending}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-slate-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-cyan-500 peer-disabled:opacity-50"></div>
              </label>
            </div>
          </div>
        )}

        {updatePreferencesMutation.isError && (
          <p className="mt-4 text-sm text-red-400">Failed to update preferences. Please try again.</p>
        )}
      </div>
    </div>
  );
}

export default SettingsPage;
